import { useState, useContext } from 'react'
import AuthContext from 'contexte/AuthContext'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import { launchError, launchSuccess } from 'components/notifications'
import Badge from 'components/Badge/Badge'
import TickerImage from 'components/DataComponents/TickerImage/TickerImage'
import { formatNumberWithSpaces } from 'utils/numbers'
import ConfirmModal from 'components/ConfirmModal/ConfirmModal'
import BBookEmpty from './components/BBookEmpty'
import BBookInfo from './components/BBookInfo'
import BBookListToolbar, { BBookPager } from './components/BBookListToolbar'
import BBookReturnable from './components/BBookReturnable'
import { money, signedMoney } from './report/pnlCharts'
import { usePolling, useT } from './hooks'
import { leverageLabel } from './leverage'
import { recall, remember } from './cache'
import { sortedHead, sortFromHead } from './sorting'
import { fetchBBookPositions, forcePositionToABook } from 'services/bBookServices'

const REFRESH_MS = 15000
const SIDE_BADGE = { LONG: '#0F766E', SHORT: '#C2410C' } // darker than the shared badge colours, so white text reaches 4.5:1
const COLUMNS = ['user', 'ticker', 'side', 'leverage', 'invested', 'quantity', 'entry_price', 'market_value', 'unrealized_pl', 'action']
const SORTABLE = COLUMNS.filter((key) => key !== 'action')

// The two moves between the books, on one screen: what the house carries and could hedge (B -> A), and what it hedged and could
// take back (A -> B). Both lists refresh on the same beat; a failed refresh keeps the last rows on screen, flagged as stale.
const BBookMoves = () => {
  const { t } = useT()
  const { user } = useContext(AuthContext)

  const [page, setPage] = useState(0)
  const [limit, setLimit] = useState(10)
  const [sort, setSort] = useState(null) // null = the engine's order (biggest cost basis first)
  const [filters, setFilters] = useState({ side: null, ticker: '' })
  const setFilter = (key) => (value) => { setFilters((prev) => ({ ...prev, [key]: value })); setPage(0) }
  const filtering = !!(filters.side || filters.ticker)
  const [positions, setPositions] = useState(() => recall('moves:positions') ?? [])
  const [at, setAt] = useState(null)
  const [stale, setStale] = useState(false)
  const [toForce, setToForce] = useState(null)
  const [forcing, setForcing] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [pendingSince, setPendingSince] = useState(null)

  // a move takes a few seconds (hedge, fill, flip): poll again soon after asking for one instead of waiting for the next beat
  const nudge = () => {
    setPendingSince(new Date())
    for (const ms of [2500, 7000, 12000]) setTimeout(() => setRefreshKey((k) => k + 1), ms)
    setTimeout(() => setPendingSince(null), 12500)
  }

  usePolling(async () => {
    try {
      setPositions(remember('moves:positions', await fetchBBookPositions(user, page, limit, sort, filters)))
      setAt(new Date())
      setStale(false)
    } catch (e) {
      if (!at) launchError(t('notifications.b_book.positions.fetch_error'))
      setStale(true)
    }
  }, REFRESH_MS, [user, page, limit, sort, filters, refreshKey])

  const columnLabel = (key) => t(`pages.b_book.positions.table.${key}`)
  const onHeaderClick = (clicked) => { setSort(sortFromHead(clicked, COLUMNS, columnLabel, sort, SORTABLE)); setPage(0) }

  const confirmForce = async () => {
    const position_id = toForce?.position_id
    try {
      setForcing(true)
      await forcePositionToABook(user, position_id)
      setPositions(prev => prev.filter((position) => position.position_id !== position_id))
      launchSuccess(t('notifications.b_book.positions.force_success'))
      nudge()
    } catch (e) {
      launchError(t(e?.response?.status === 503 ? 'notifications.b_book.engine_not_listening' : 'notifications.b_book.positions.force_error'))
    } finally {
      setForcing(false)
      setToForce(null)
    }
  }

  const tbody = (positions || []).map((item) => ({
    data: [
      `${item.firstName} ${item.lastName}`,
      <TickerImage ticker={item.ticker} />,
      <Badge color={SIDE_BADGE[String(item.side).toUpperCase()]} text={String(item.side).toUpperCase()} />,
      <span className='b_book_num'>{Number(item.leverage_rate) > 0 ? leverageLabel(Number(item.leverage_rate)) : '—'}</span>,
      <span className='b_book_num'>{item.invested_eur === null || item.invested_eur === undefined ? '—' : money(item.invested_eur)}</span>,
      <span className='b_book_num'>{formatNumberWithSpaces(item.qty)}</span>,
      <span className='b_book_num'>{formatNumberWithSpaces(item.avg_entry_price)}</span>,
      <span className='b_book_num'>{item.market_value === null ? '—' : money(item.market_value)}</span>,
      item.house_unrealized_pl === null ? '—' : (
        <span className='b_book_stack'>
          <span className={`b_book_num ${item.house_unrealized_pl > 0 ? 'b_book_position_gain' : item.house_unrealized_pl < 0 ? 'b_book_position_loss' : ''}`}>
            {signedMoney(item.house_unrealized_pl)}
          </span>
          {item.house_since_return && <span className='b_book_hint b_book_cell_hint'>{t('pages.b_book_moves.since_return', { price: formatNumberWithSpaces(item.house_entry_price) })}</span>}
        </span>
      ),
      <button className='b_book_row_button b_book_row_button_danger' onClick={(e) => { e.stopPropagation(); setToForce(item) }}
        aria-label={t('pages.b_book.positions.force_label', { ticker: item.ticker, user: `${item.firstName} ${item.lastName}` })}>
        {t('pages.b_book.positions.force')}
      </button>
    ],
    utils: { id: item.position_id }
  }))

  return (
    <>
        <div className='b_book_title_component'>
          <h1 className='b_book_page_title'>{t('pages.b_book_moves.title')}</h1>
        </div>
        <p className='b_book_hint b_book_page_lede'>{t('pages.b_book_moves.hint')}</p>
        {pendingSince && <p className='b_book_hint b_book_pending' role='status'>{t('pages.b_book_moves.pending', { time: pendingSince.toLocaleTimeString('fr-FR') })}</p>}

        <section aria-labelledby='b-book-positions-title'>
          <div className='b_book_title_component'>
            <h2 id='b-book-positions-title'>{t('pages.b_book.positions.title')}</h2>
            <BBookInfo label={t('pages.b_book.positions.title')} text={t('pages.b_book_info.positions')} />
            {at && (
              <span className={`b_book_live_stamp ${stale ? 'b_book_live_stamp_stale' : ''}`}>
                <span className='b_book_live_dot' aria-hidden='true' />
                {stale ? t('pages.b_book_pnl.live.stale') : t('pages.b_book_pnl.live.updated', { time: at.toLocaleTimeString('fr-FR') })}
              </span>
            )}
          </div>
          <p className='b_book_hint'>{t('pages.b_book.positions.hint')}</p>

          <div className='b_book_card'>
            {(tbody.length > 0 || page > 0 || filtering) && (
              <BBookListToolbar searchLabel={t('pages.b_book.filters.ticker')} searchPlaceholder={t('pages.b_book.filters.ticker_placeholder')}
                search={filters.ticker} setSearch={setFilter('ticker')} side={filters.side} setSide={setFilter('side')} limit={limit} setLimit={(value) => { setLimit(value); setPage(0) }} />
            )}
            {tbody.length || page > 0 ? (
              <div className='table-container b_book_sortable'>
                <div className='table-column'>
                  <Tableau
                    thead={sortedHead(COLUMNS, columnLabel, sort)}
                    tbody={tbody}
                    onHeaderClick={onHeaderClick}
                  />
                </div>
                <BBookPager page={page} hasNext={tbody.length >= limit} onChange={setPage} />
              </div>
            ) : (
              <BBookEmpty title={t(filtering ? 'pages.b_book.filters.empty' : 'pages.b_book.positions.empty')} hint={filtering ? null : t('pages.b_book.positions.empty_hint')} />
            )}
          </div>
        </section>

        <BBookReturnable refreshKey={refreshKey} onMoved={nudge} />

        <ConfirmModal
          isOpen={!!toForce}
          loading={forcing}
          title={t('pages.b_book.positions.confirm_title')}
          message={toForce ? t('pages.b_book.positions.confirm_message', { user: `${toForce.firstName} ${toForce.lastName}`, side: toForce.side, qty: toForce.qty, ticker: toForce.ticker }) : ''}
          confirmLabel={t('pages.b_book.positions.confirm')}
          cancelLabel={t('pages.b_book.positions.cancel')}
          onConfirm={confirmForce}
          onClose={() => setToForce(null)}
        />
    </>
  )
}

export default BBookMoves
